import Box, { type BoxProps } from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import lottie from 'lottie-web/build/player/lottie_light'
import React, { useEffect, useRef } from 'react'

import { Z_INDEX } from 'theme/zIndex'

import hhloader from './hh_loader.json'

const { LOADER } = Z_INDEX

type LoaderProps = {
  left?: number
  sx?: BoxProps['sx']
  contained?: boolean
}

// `contained` fills the nearest positioned ancestor and leaves body scroll alone. The default form
// is a whole-app blocker, so using it for in-content loading flashes over the navbar and flips the
// scrollbar on every render.
const Loader: React.FC<LoaderProps> = ({ left = 0, sx, contained = false }) => {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return undefined

    if (!contained) document.body.style.overflow = 'hidden'
    const loaderInstance = lottie.loadAnimation({
      container,
      animationData: hhloader,
    })
    return () => {
      loaderInstance.destroy()
      if (!contained) document.body.style.overflow = 'visible'
    }
  }, [contained])

  const theme = useTheme()
  const fill = contained
    ? ({ position: 'absolute', inset: 0, width: '100%', height: '100%' } as const)
    : ({ position: 'fixed', top: 10, left, width: '100vw', minWidth: '100vw', height: '100vh', minHeight: '100vh' } as const)

  return (
    <Box
      ref={containerRef}
      alignItems="center"
      display="flex"
      id="hh_loader_container"
      justifyContent="center"
      sx={{
        backgroundColor: `${theme.palette.background.default}bd`,
        '& svg': { width: '194px!important', height: '194px!important', position: 'absolute' },
        ...fill,
        ...sx,
      }}
      zIndex={LOADER}
    />
  )
}

export default Loader
